import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
} from "react";
import { isTurboLike, isSuperTurbo } from "../../../utils/speedHelpers";
import StorageManager from "../../../utils/StorageManager";
import TickerTimeout from "../../../utils/TickerTimeout";
import { Container, Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { Assets } from "@pixi/assets";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import {
  useSlotRegistry,
  useSlot,
  getCounterScale,
} from "../../../Contexts/SlotRegistryContext";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { Settings } from "../../../settings";
import { useSelector, useDispatch } from "react-redux";
import {
  selectPotCollectionActive,
  selectPotCollectionTriggered,
  selectIsSpinning,
  selectMenuActive,
  selectIntroPopupActive,
  selectReplaySessionActive,
  selectInfoPopupActive,
  selectShowPotCollectionIntro,
  selectPotCollectionOutroTransitionActive,
  selectShowGameAfterTransition,
  selectIsFeatureActive,
  selectPotCollectionReloadPhase,
  selectPotCollectionReelsReady,
} from "../../../State/selectors";
import {
  setPotCollectionActive,
  setPotCollectionTriggered,
  setPotCollectionTransitionActive,
  setPotCollectionOutroTransitionActive,
  setWinAnimationsPlaying,
  resetPotCollection,
  clearFeatureStateToBase,
  setFeatureSpinActive,
  setFeatureSpinComplete,
  setCriticalAnimationPlaying,
  setUserInterfaceDisabled,
  setResetMainGameVisualEffects,
  setAutoplaySpeedNoSave,
  resumeAutoPlay,
  closeConnectionPopup,
  syncGameState,
  setShowPotCollectionReload,
  applyReplayWin,
} from "../../../State/actions";
import { useInitialIdleAnimations } from "../../../Hooks/useLandingAnimations";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import {
  selectJackpotValues,
  selectCurrentRound,
  selectPreReplayPotProgress,
  selectPotProgress,
} from "../../../State/Slices/gameSettingsSlice";
import { selectAutoPlaySpeed, selectAutoPlayActive } from "../../../State/Slices/settingsSlice";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import GameViewController from "./GameViewController";
import ReelSet from "../../../Games/Main/ReelSet/ReelSet";
import OverlayReelSet from "../../../Games/Main/OverlayReelSet/OverlayReelSet";
import DimReelSet from "../../../Games/Shared/DimReelSet/DimReelSet";
import TensionReelSet from "../../../Games/Main/TensionReelSet/TensionReelSet";
import WinBoxContainer from "../../../Games/Shared/WinBoxReelSet/WinBoxContainer";
import MiniPaytable from "../../../Games/Shared/MiniPaytable/MiniPaytable";
import WinText from "../WinText/WinText";
import { WIN_TEXT_CONFIG } from "../WinText/WinTextController";
import {
  attemptFullscreenToggle,
  isPhysicalDesktop,
  isInFullscreen,
} from "../../../utilities";
import store from "../../../State/Store/store";
import PotChargingAnimator from "../../../Engine/PotCharging/PotChargingAnimator";

import PotCollectionReelSet from "../../../Games/PotCollection/PotCollectionReelSet";
import PotCollectionOverlayReelSet from "../../../Games/PotCollection/PotCollectionOverlayReelSet";
import PotCollectionCollectionLayer from "../../../Games/PotCollection/PotCollectionCollectionLayer";
import PotCollectionRespinCounter from "../../../Games/PotCollection/PotCollectionRespinCounter";

// Rate limiting for background clicks
let lastBackgroundClickTime = 0;
const MIN_BACKGROUND_CLICK_GAP = 100;
let backgroundClickThrottleActive = false;
let backgroundClickThrottleTimeout = null;

// Shared X offset to center symbols within the spine clipping mask.
// Both reel_container and overlay_reel_container MUST use this — they always stack.
// Computed from clipping mask vertices in game_view.json (symbols_mask_start):
//   The mask is not a perfect rectangle — right edge varies by 14px.
//   Use inner (tightest) edges to prevent clipping:
//     Left:    max(-538.59, -539.66) = -538.59
//     Right:   min(576.92, 562.52)   =  562.52
//     Center:  (-538.59 + 562.52) / 2 = 11.97 ≈ 12
const REELSET_X_OFFSET = 0;
const REELSET_Y_OFFSET = 1;

// Tension container offset — tune independently from overlay
const TENSION_Y_OFFSET = -22;

// Divider base overlay configuration — fine-tuning multipliers and offsets
// Follows the same pattern as ReelBackController's SPINE_CONFIG
export const DIVIDER_BASE_CONFIG = {
  widthMultiplier: 0.6056,   // Scale relative to computed reelset width
  heightMultiplier: 1.0,  // Scale relative to computed reelset height
  xOffset: 0,             // Horizontal offset in pixels (positive = right)
  yOffset: 0,             // Vertical offset in pixels (positive = down)
};

// Divider feature overlay configuration — used during bonus/feature skin
export const DIVIDER_FEATURE_CONFIG = {
  widthMultiplier: 1.0,   // Scale relative to computed reelset width
  heightMultiplier: 1.0,  // Scale relative to computed reelset height
  xOffset: 0,             // Horizontal offset in pixels (positive = right)
  yOffset: 0,             // Vertical offset in pixels (positive = down)
};

/**
 * GameView — Unified spine scene component.
 *
 * Renders the game_view spine which contains background, reel frame, masks,
 * dividers, jackpots, and pots. Injects ReelSet, OverlayReelSet, and DimReelSet
 * into spine bone slots via SlotRegistry.
 *
 * Replaces both BackgroundAnimation and GameContainer when GAME_VIEW.ENABLED.
 */
const GameView = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const reelContainerRef = useRef(null);
  const overlayContainerRef = useRef(null);
  const tensionContainerRef = useRef(null);
  const potTrailContainerRef = useRef(null);
  const potChargingAnimatorLocalRef = useRef(null);
  const symbolHeightRef = useRef(null);

  const {
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    dividerDimensions,
    reelSpacing,
    device,
    orientation,
    isMobileSmall,
  } = useDimensions();
  const slotRegistry = useSlotRegistry();
  const { dimReelSetRef, potChargingAnimatorRef, potControllersRef, globalLineWinTextRef, lineWinTextRef, winBoxContainerRef, gameViewControllerRef, potCollectionReelRefs, potCollectionRespinCounterRef, potCollectionCollectionLayerRef, snapClearAllRef } = useReelRefs();

  const potCollectionActive = useSelector(selectPotCollectionActive);
  const potCollectionTriggered = useSelector(selectPotCollectionTriggered);
  const potCollectionReloadPhase = useSelector(selectPotCollectionReloadPhase);
  const potCollectionReelsReady = useSelector(selectPotCollectionReelsReady);
  const showPotCollectionIntro = useSelector(selectShowPotCollectionIntro);
  const showGameAfterTransition = useSelector(selectShowGameAfterTransition);

  const menuActive = useSelector(selectMenuActive);
  const jackpotValues = useSelector(selectJackpotValues);

  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();

  const isFeatureActive = useSelector(selectIsFeatureActive);
  const fullscreenSupported = useSelector((state) => state.settings.fullscreenSupported);

  // Physical device capability (screen size/touch) is stable for the session
  const isActualDesktop = useMemo(() => isPhysicalDesktop(), []);

  // The full-screen click-catcher below only ever does something (enters
  // fullscreen) when this is true — mirrors handleBackgroundClick's own guard.
  // Without this, the catcher shows a pointer cursor over the entire game on
  // desktop even though clicking it is a no-op there.
  const backgroundClickIsActionable =
    !isActualDesktop && fullscreenSupported && !isInFullscreen();

  // Counter-scale to undo the spine's inherited scale on injected containers
  const spineCounterScale = useMemo(
    () => getCounterScale(symbolScale),
    [symbolScale],
  );

  // Initialize controller when container is ready
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      const realDevice = isMobileSmall
        ? PixiSettings.DEVICE.MOBILE_SMALL
        : device;

      controllerRef.current = new GameViewController(
        containerRef.current,
        dimensions,
        symbolWidth,
        symbolHeight,
        symbolScale,
        realDevice,
        orientation,
        slotRegistry,
      );
      // Expose to shared ReelRefs so other components can trigger transitions
      if (gameViewControllerRef) {
        gameViewControllerRef.current = controllerRef.current;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Inject reel_container into spine's attachment_slot_placeholder ───
  const reelSlot = useSlot("gameView.reelSet");
  useEffect(() => {
    if (!reelSlot || !reelContainerRef.current) return;
    const node = reelContainerRef.current;
    reelSlot.addChild(node);

    return () => {
      if (node.parent === reelSlot) {
        reelSlot.removeChild(node);
      }
    };
  }, [reelSlot]);

  // ─── Inject overlay_container into spine's attachment_slot_placeholder_front ───
  const overlaySlot = useSlot("gameView.overlayReelSet");
  useEffect(() => {
    if (!overlaySlot || !overlayContainerRef.current) return;
    const node = overlayContainerRef.current;
    overlaySlot.addChild(node);

    return () => {
      if (node.parent === overlaySlot) {
        overlaySlot.removeChild(node);
      }
    };
  }, [overlaySlot]);

  // ─── Inject tension_container into overlay spine slot (above DimReelSet) ───
  const tensionSlot = useSlot("gameView.overlayReelSet");
  useEffect(() => {
    if (!tensionSlot || !tensionContainerRef.current) return;
    const node = tensionContainerRef.current;
    tensionSlot.addChild(node);

    return () => {
      if (node.parent === tensionSlot) {
        tensionSlot.removeChild(node);
      }
    };
  }, [tensionSlot]);

  // ─── Apply counter-scale to undo spine's inherited scale ───
  useEffect(() => {
    if (reelContainerRef.current) {
      reelContainerRef.current.scale.set(spineCounterScale);
    }
    if (overlayContainerRef.current) {
      overlayContainerRef.current.scale.set(spineCounterScale);
    }
    if (tensionContainerRef.current) {
      tensionContainerRef.current.scale.set(spineCounterScale);
    }
  }, [spineCounterScale]);

  // ─── Jackpot text injection into spine slots ───
  useEffect(() => {
    if (!controllerRef.current || !jackpotValues) return;
    if (!FEATURES_CONFIG.JACKPOTS.ENABLED) return;

    const ctrl = controllerRef.current;
    if (!ctrl.spine) return;

    if (Object.keys(ctrl.jackpotTextInstances).length === 0) {
      ctrl.initializeJackpotTexts(jackpotValues);
    } else {
      ctrl.updateJackpotValues(jackpotValues);
    }

    // Initialize coin counter at the same time (hidden in base game skin)
    // so the bone is found before any skin swap. Updated to real values at midpoint.
    if (FEATURES_CONFIG.POT_COLLECTION.ENABLED && !ctrl._coinCounterInstance) {
      ctrl.initializeCoinCounter(0, 15);
      ctrl.setCoinCounterVisible(false); // Hidden until bonus activates
    }
  }, [jackpotValues]);

  // Handle dimension changes for responsive design
  useEffect(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;

    if (controllerRef.current) {
      controllerRef.current.resize(
        dimensions,
        symbolWidth,
        symbolHeight,
        symbolScale,
        realDevice,
        orientation,
      );
    }
  }, [dimensions, symbolWidth, symbolHeight, symbolScale, device, orientation, isMobileSmall]);

  // Switch game state skin when feature state changes (main ↔ bonus)
  useEffect(() => {
    if (!controllerRef.current) return;
    controllerRef.current.setGameState(isFeatureActive ? "bonus" : "main");

    if (potCollectionActive) {
      // Entering bonus — play feature idle animation and start rain FX.
      // During normal entry, the cloud transition midpoint also does this,
      // but on mid-bonus reload the transition is skipped.
      controllerRef.current.playFeatureIdle();
      controllerRef.current._startRain();
    } else {
      // Returning to base game — hide respin counter, restore pot labels, stop rain
      controllerRef.current.setRespinCounterVisible(false);
      controllerRef.current.showAllPotTexts();
      controllerRef.current._stopRain();
    }
  }, [isFeatureActive, potCollectionActive]);


  // Handle background click to toggle fullscreen
  const handleBackgroundClick = useCallback(
    (event) => {
      if (backgroundClickThrottleActive) return;

      backgroundClickThrottleActive = true;
      if (backgroundClickThrottleTimeout) {
        clearTimeout(backgroundClickThrottleTimeout);
      }
      backgroundClickThrottleTimeout = setTimeout(() => {
        backgroundClickThrottleActive = false;
        backgroundClickThrottleTimeout = null;
      }, MIN_BACKGROUND_CLICK_GAP);

      const now = Date.now();
      if (now - lastBackgroundClickTime < MIN_BACKGROUND_CLICK_GAP) return;
      lastBackgroundClickTime = now;

      if (event?.stopPropagation) event.stopPropagation();

      const isActualDesktop = isPhysicalDesktop();
      const alreadyInFullscreen = isInFullscreen();
      const fullscreenSupported = store.getState().settings.fullscreenSupported;

      if (!isActualDesktop && !alreadyInFullscreen && fullscreenSupported) {
        attemptFullscreenToggle(dispatch, device);
      }
    },
    [dispatch, device],
  );

  // Memoize sprite dimensions for click detection
  const spriteDimensions = useMemo(
    () => ({
      width: dimensions.width,
      height: dimensions.height,
    }),
    [dimensions.width, dimensions.height],
  );

  // Cleanup throttle timeout on unmount
  useEffect(() => {
    return () => {
      if (backgroundClickThrottleTimeout) {
        clearTimeout(backgroundClickThrottleTimeout);
        backgroundClickThrottleTimeout = null;
        backgroundClickThrottleActive = false;
      }
    };
  }, []);

  // ─── POT Charging Animator lifecycle ───
  useEffect(() => {
    if (!FEATURES_CONFIG.POT_CHARGING.ENABLED) return;
    if (!potTrailContainerRef.current) return;

    // Create animator if not yet initialized
    if (!potChargingAnimatorLocalRef.current) {
      const spine = controllerRef.current?.spine || null;
      potChargingAnimatorLocalRef.current = new PotChargingAnimator(
        potTrailContainerRef.current,
        spine,
        symbolWidth,
        symbolHeight,
      );
      // Expose via shared ref so useLandingAnimations can access it
      if (potChargingAnimatorRef) {
        potChargingAnimatorRef.current = potChargingAnimatorLocalRef.current;
      }
      // Expose pot controllers so trail arrival can trigger playHit()
      if (potControllersRef && controllerRef.current?.potControllers) {
        potControllersRef.current = controllerRef.current.potControllers;
      }
    }

    return () => {
      if (potChargingAnimatorLocalRef.current) {
        potChargingAnimatorLocalRef.current.destroy();
        potChargingAnimatorLocalRef.current = null;
        if (potChargingAnimatorRef) {
          potChargingAnimatorRef.current = null;
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep animator's spine ref in sync (spine may re-create on resize)
  useEffect(() => {
    if (!FEATURES_CONFIG.POT_CHARGING.ENABLED) return;
    const animator = potChargingAnimatorLocalRef.current;
    if (animator && controllerRef.current?.spine) {
      animator.setSpine(controllerRef.current.spine);
    }
    // Keep potControllers ref in sync after resize
    if (potControllersRef && controllerRef.current?.potControllers) {
      potControllersRef.current = controllerRef.current.potControllers;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions]);

  // Keep animator dimensions in sync
  useEffect(() => {
    if (!FEATURES_CONFIG.POT_CHARGING.ENABLED) return;
    const animator = potChargingAnimatorLocalRef.current;
    if (animator) {
      animator.resize(symbolWidth, symbolHeight);
    }
  }, [symbolWidth, symbolHeight]);

  const isSpinning = useSelector(selectIsSpinning);


  // ── POT Trigger Animation (bonus entrance — GDD §12.2) ────────────────────
  // NOTE: playTrigger() is now called from onArrival in useLandingAnimations
  // when the lightning trail physically hits the POT, not immediately on
  // featureState change. This ensures the grow-to-11 + win burst animation
  // is timed to the visual impact, not the API response.
  // See useLandingAnimations.js → fireTrail → onArrival callback.


  // ── POTs after a REPLAY, back to the stages the LIVE game left them on ─────
  //
  // A replay spin writes its recorded round through the same path a live one
  // does, so `useLandingAnimations` grows the pots to whatever the replayed
  // round reached. Redux is already protected — `potProgress` is in
  // gameSettingsSlice's REPLAY_SNAPSHOT_FIELDS, so `endReplaySession` hands the
  // live values straight back — but the PotController instances are not Redux.
  // Nothing was telling them, so the orbs stayed at the replay's high-water
  // mark over a live game that was nowhere near it.
  //
  // Assigned rather than grown. `setStage` plays the collect -> in -> idle
  // chain, which is a lie in both directions here: the player did not earn
  // these stages now, and coming back from a big replayed round the move is
  // DOWNWARDS, which that chain cannot express at all. Same treatment the
  // bonus exit gives them — stage, reactivate, idle.
  //
  // On the falling edge only, and the ordering is safe: `endReplaySession`
  // clears `sessionActive` and restores `potProgress` in the same dispatch, so
  // by this render the snapshot is already back.
  const replaySessionActive = useSelector(selectReplaySessionActive);
  const prevReplaySessionRef = useRef(replaySessionActive);
  // The replayed round's own stages, once its init response lands.
  const potProgressValue = useSelector(selectPotProgress);
  // The LIVE stages, parked at session start — used only to tell whether
  // potProgress has been replaced by the replayed round's yet.
  const preReplayPots = useSelector(selectPreReplayPotProgress);
  // One application per session — see the effect below.
  const replayPotsAppliedRef = useRef(false);

  // ── POTs on the way IN: the stages the REPLAYED round started on ──
  //
  // The mirror of the effect below. `fetchReplayInit` reports `progress.pots`
  // for the round being watched and gameSettingsSlice writes it into
  // potProgress, so by the time that value changes the orbs can be put where
  // that round had them — otherwise the player watches an old round with
  // TODAY'S pots standing behind it.
  //
  // Keyed on potProgress rather than on the session flag, because the flag goes
  // true at `startReplaySession` and the init response lands a moment LATER; an
  // effect on the rising edge alone would run before the data existed.
  //
  // ONCE per session. After the first application the replayed steps drive the
  // orbs exactly as live spins do — bolts land, `useLandingAnimations` grows
  // them — and re-applying on every potProgress change would pre-empt that
  // growth and flatten the animation the replay is supposed to show.
  useEffect(() => {
    if (!FEATURES_CONFIG.POT_CHARGING.ENABLED) return;

    if (!replaySessionActive) {
      replayPotsAppliedRef.current = false;
      return;
    }
    if (replayPotsAppliedRef.current || !potProgressValue) return;

    // ── Wait for the value to actually BECOME the replayed round's ──
    //
    // `sessionActive` turns true at `startReplaySession`; the init response
    // lands a moment later. So on the first run here `potProgress` is still the
    // LIVE value — applying it is a no-op the player cannot see, and latching
    // the ref on it meant the real stages were then skipped. That is exactly
    // what made this do nothing.
    //
    // The live value is parked by that same dispatch, so `preReplaySnapshot`
    // holds the very same OBJECT. Identity is therefore an exact test for "the
    // init has not replaced it yet" — no extra state, no guessing at timing.
    if (potProgressValue === preReplayPots) return;

    const controllers = potControllersRef?.current;
    if (!controllers) return;

    replayPotsAppliedRef.current = true;
    const keyMap = { C: "collect", D: "double", M: "mystery" };
    for (const [apiChar, potType] of Object.entries(keyMap)) {
      const ctrl = controllers[potType];
      if (!ctrl) continue;
      // Assigned, never grown — the same reasoning as the exit below: the
      // player did not earn these now, and entering a round with emptier pots
      // than the live game is a move DOWNWARDS, which the growth chain cannot
      // express.
      ctrl.stage = potProgressValue[apiChar]?.stage ?? 0;
      ctrl.reactivate();
      // ── ENTRANCE, not a snap ──
      //
      // `playEntrance()` runs the rig's own in/state_N -> idle/state_N pair, so
      // the orb shrinks away and grows back into its new stage instead of
      // blinking into it. Same call the intro popup uses when the pots first
      // appear, so it is the artist's timing rather than a tween invented here
      // — and it costs nothing to keep in step with a re-export.
      //
      // Falls back to idle on its own when the stage has no `in` clip.
      ctrl.playEntrance();
    }
  }, [replaySessionActive, potProgressValue, preReplayPots, potControllersRef]);

  useEffect(() => {
    if (!FEATURES_CONFIG.POT_CHARGING.ENABLED) return;
    const prev = prevReplaySessionRef.current;
    prevReplaySessionRef.current = replaySessionActive;

    // Leaving replay, not entering it.
    if (!prev || replaySessionActive) return;

    const controllers = potControllersRef?.current;
    if (!controllers) return;

    const potProgress = store.getState().game?.potProgress || {};
    const keyMap = { C: "collect", D: "double", M: "mystery" };
    for (const [apiChar, potType] of Object.entries(keyMap)) {
      const ctrl = controllers[potType];
      if (!ctrl) continue;
      ctrl.stage = potProgress[apiChar]?.stage ?? 0;
      ctrl.reactivate();
      // Same entrance on the way back — see the note in the effect above. The
      // return to the live stages is as much a change as the arrival was.
      ctrl.playEntrance();
    }
  }, [replaySessionActive, potControllersRef]);

  // ── POT Entrance Animation (after intro popup closes) ──────────────────────
  // Plays in/state_N → idle/state_N on all 3 POTs when the game first becomes visible.
  const introPopupActive = useSelector(selectIntroPopupActive);
  const prevIntroPopupRef = useRef(introPopupActive);

  useEffect(() => {
    if (!FEATURES_CONFIG.POT_CHARGING.ENABLED) return;
    const prev = prevIntroPopupRef.current;
    prevIntroPopupRef.current = introPopupActive;

    // Only fire on intro popup close (true → false)
    if (!prev || introPopupActive) return;

    const controllers = potControllersRef?.current;
    if (!controllers) return;

    // Sync pot stages from API progress before entrance animation
    const potProgress = store.getState().game?.potProgress || {};
    const keyMap = { C: "collect", D: "double", M: "mystery" };
    for (const [apiChar, potType] of Object.entries(keyMap)) {
      const ctrl = controllers[potType];
      if (!ctrl) continue;
      const apiStage = potProgress[apiChar]?.stage ?? 0;
      if (apiStage > 0) {
        ctrl.stage = apiStage; // Set directly — no animation, just sync
      }
    }

    for (const controller of Object.values(controllers)) {
      controller.playEntrance();
    }

    // ── PC Intro Reload: show win state on triggered POTs ──
    // On reload, pots are already at stage 11 — skip trigger animation,
    // just play win loop with sound.
    const pcState = store.getState().potCollection;
    if (pcState?.potCollectionTriggered && pcState?.reloadPhase === "intro") {
      const respinGameType = pcState.respinGameType || "";
      for (const [typeChar, potType] of Object.entries(keyMap)) {
        if (respinGameType.includes(typeChar) && controllers[potType]) {
          controllers[potType].playWinLoop();
        }
      }
    }
  }, [introPopupActive, potControllersRef]);

  // ── Helper: show respins-left popup after FR info popup closes ──────────────
  // Shared by both reload paths (showGameAfterTransition and intro popup close).
  // When respinsLeft === 0, skips popup entirely and just unlocks UI.
  const showReloadPopupWhenReady = useCallback((respinsLeft, initialDelay = false) => {
    if (respinsLeft === 0) {
      // No popup needed — poll for intro/info popups to close, then unlock UI
      const waitAndUnlock = () => {
        const s = store.getState();
        if (selectIntroPopupActive(s) || selectInfoPopupActive(s)) {
          new TickerTimeout(() => waitAndUnlock(), 100);
        } else {
          dispatch(setCriticalAnimationPlaying(false));
          dispatch(setUserInterfaceDisabled(false));
        }
      };
      if (initialDelay) {
        new TickerTimeout(() => waitAndUnlock(), 100);
      } else {
        waitAndUnlock();
      }
    } else {
      const showPopup = () => {
        if (selectInfoPopupActive(store.getState())) {
          // FR info popup still showing — poll until it closes
          new TickerTimeout(() => showPopup(), 100);
        } else {
          dispatch(setShowPotCollectionReload(true));
        }
      };
      if (initialDelay) {
        new TickerTimeout(() => showPopup(), 100);
      } else {
        showPopup();
      }
    }
  }, [dispatch]);

  // ── POT Collection Mid-Bonus Reload Setup ─────────────────────────────────
  // When the intro overlay completes (showGameAfterTransition) and we are in
  // mid-bonus reload, set up the bonus scene directly — no cloud transition.
  // Also re-runs after ErrorRecovery rehydrates PC (reloadPhase / active flip).
  const midBonusSetupDoneRef = useRef(false);

  // Allow setup again after PC is fully cleared (error recovery wipe → rehydrate)
  useEffect(() => {
    if (!potCollectionActive && potCollectionReloadPhase === "none") {
      midBonusSetupDoneRef.current = false;
    }
  }, [potCollectionActive, potCollectionReloadPhase]);

  useEffect(() => {
    if (!FEATURES_CONFIG.POT_COLLECTION.ENABLED) return;
    if (!showGameAfterTransition) return;
    if (midBonusSetupDoneRef.current) return;

    const pcState = store.getState().potCollection;
    const reloadPhase = pcState?.reloadPhase;
    // Mid-bonus (respins) must rebuild. Collection/EOB only on cold load with LS.
    if (reloadPhase !== "respins" && reloadPhase !== "collection") return;

    // After error recovery remount, wait until bonus reels report ready
    // so coin blanks / counters can be applied on a live scene.
    if (reloadPhase === "respins" && !potCollectionReelsReady) {
      return;
    }

    midBonusSetupDoneRef.current = true;

    // 0. Clear ALL base game win state immediately.
    //    snapClearAll kills the win cycling loop (isAnimatingRef), snap-clears dim overlay,
    //    hides win lines/boxes, stops overlay animations, restores symbol textures.
    //    Normally done at cloud transition midpoint — skipped on reload.
    if (snapClearAllRef.current) {
      snapClearAllRef.current();
    }
    triggerInitialIdleAnimations(true);        // Also transition spines to idle
    dispatch(setWinAnimationsPlaying(false));  // Stop Redux win cycling flag

    // 1. Reset trigger flag (one-shot)
    dispatch(setPotCollectionTriggered(false));

    // 2. Force bonus game to start in normal auto speed
    //    Use NoSave variant so localStorage keeps the player's turbo preference
    //    for restoration when returning to base game.
    const currentSpeed = store.getState().settings.autoPlaySpeed;
    if (isTurboLike(currentSpeed)) {
      dispatch(setAutoplaySpeedNoSave('auto'));
    }

    // 3. GameView spine bonus skin, feature_idle, and rain are handled reactively
    //    by the isFeatureActive effect (potCollectionActive triggers it).

    // 4. Deactivate non-triggered POTs, switch triggered ones to idle
    const respinGameType = pcState.respinGameType || "";
    const typeToTier = { M: "mystery", D: "double", C: "collect" };
    const controllers = potControllersRef?.current;
    if (controllers) {
      for (const [typeChar, tier] of Object.entries(typeToTier)) {
        if (!respinGameType.includes(typeChar) && controllers[tier]) {
          controllers[tier].deactivate();
        } else if (controllers[tier]) {
          controllers[tier].playIdle();
        }
      }
    }

    // 5. Apply feature skin for active pot text labels
    if (controllerRef.current) {
      controllerRef.current.setFeatureSkin(respinGameType || null);
    }

    // 6. Show respin counter at current value
    if (controllerRef.current) {
      controllerRef.current.setRespinCounterVisible(true);
    }
    const counterCtrl = potCollectionRespinCounterRef?.current;
    if (counterCtrl?.counterSpine?.state) {
      const respins = pcState.respinsLeft ?? 3;
      // Static states: counter_full (3), counter_empty (0)
      // For 1 or 2: play the transition that ENDS at that value, jumped to last frame
      const animMap = { 3: "counter_full", 2: "counter_3to2", 1: "counter_2to1", 0: "counter_empty" };
      const animName = animMap[respins];
      if (animName && counterCtrl.hasAnimation(animName)) {
        const entry = counterCtrl.counterSpine.state.setAnimation(0, animName, false);
        // For transitions (1, 2): jump to end frame so it shows the static result
        if (respins === 1 || respins === 2) {
          entry.trackTime = entry.animationEnd;
        }
        counterCtrl.displayedValue = respins;
        counterCtrl._syncPlayCounter(respins);
      }
    }

    // 7. Update coin counter (GVC ref not available when PotCollectionControlContext effect fires)
    if (controllerRef.current) {
      controllerRef.current.updateCoinCounter(pcState.coinCount || 0, 15);
      controllerRef.current.setCoinCounterVisible(true);
    }

    // 8. Hide underlying blank textures at positions where coins already exist.
    //    In the natural flow, blanks show first and coins eject onto them.
    //    On reload, overlay spines render coins immediately — blanks behind them must be hidden.
    const reelData = pcState.initialRespinScreen;
    if (potCollectionReelRefs?.current && Array.isArray(reelData)) {
      const blankId = 16; // BLANK_SYMBOL_ID
      for (let r = 0; r < reelData.length; r++) {
        const reelRef = potCollectionReelRefs.current[r];
        if (!reelRef) continue;
        const symbols = reelRef.getSymbolRefs?.() || [];
        for (let s = 0; s < (reelData[r]?.length || 0); s++) {
          const cell = reelData[r][s];
          const cellInt = typeof cell === "string" ? parseInt(cell, 10) : cell;
          if (cellInt !== blankId) {
            // Non-blank = coin/orb position — hide underlying blank texture
            const symRef = symbols[s];
            if (symRef?.current?.hideSymbolTexture) {
              symRef.current.hideSymbolTexture();
            }
          }
        }
      }
    }

    // 8. Feature music is started by releaseMusicPlayback() when intro popup closes

    // 8. Block all UI interaction while reload popup is active
    dispatch(setCriticalAnimationPlaying(true));
    dispatch(setUserInterfaceDisabled(true));

    // 9. Show the respins left popup (after FR info popup closes)
    //    Skip when 0 respins left — popup is pointless, just unlock UI after brief delay
    showReloadPopupWhenReady(pcState.respinsLeft ?? 3, true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showGameAfterTransition,
    potCollectionActive,
    potCollectionReloadPhase,
    potCollectionReelsReady,
    dispatch,
    potControllersRef,
    potCollectionRespinCounterRef,
  ]);

  // ── POT Collection Scene Transition (intro popup close → transition → bonus) ──
  // When the PC intro popup closes while potCollectionTriggered is true,
  // play the GameView's built-in Transition animation.
  // At midpoint: clean up base game, swap to bonus scene.
  // Mirrors the RC pattern in BackgroundAnimation.jsx.
  //
  // Mid-bonus reload (reloadPhase === "respins"): bonus scene is already active.
  // Skip cloud transition entirely — set up visuals and show respins popup.
  const prevShowPCIntroRef = useRef(showPotCollectionIntro);

  useEffect(() => {
    if (!FEATURES_CONFIG.POT_COLLECTION.ENABLED) return;
    const prev = prevShowPCIntroRef.current;
    prevShowPCIntroRef.current = showPotCollectionIntro;

    // Only fire on intro popup close (true → false) while trigger is active
    if (!prev || showPotCollectionIntro) return;
    if (!potCollectionTriggered) return;
    if (!controllerRef.current) return;

    // ── Mid-bonus reload: bonus scene already active, skip cloud transition ──
    const pcState = store.getState().potCollection;
    if (pcState?.reloadPhase === "respins") {
      // 1. Reset trigger flag (one-shot)
      dispatch(setPotCollectionTriggered(false));

      // 1b. Clear dim overlay left by intro popup's triggerCoinWinAnimations.
      //     The normal cloud transition clears this at midpoint via triggerInitialIdleAnimations(true),
      //     but mid-bonus reload skips the cloud transition entirely.
      if (dimReelSetRef.current?.snapClear) {
        dimReelSetRef.current.snapClear();
      }

      // 2. Force bonus game to start in normal auto speed
      //    Use NoSave variant so localStorage keeps the player's turbo preference
      //    for restoration when returning to base game.
      const currentSpeed = store.getState().settings.autoPlaySpeed;
      if (isTurboLike(currentSpeed)) {
        dispatch(setAutoplaySpeedNoSave('auto'));
      }

      // 3. Switch GameView spine to bonus skin (already active via potCollectionActive)
      controllerRef.current.setGameState("bonus");

      // 4. Deactivate non-triggered POTs, switch triggered ones to idle
      const respinGameType = pcState.respinGameType || "";
      const typeToTier = { M: "mystery", D: "double", C: "collect" };
      const controllers = potControllersRef?.current;
      if (controllers) {
        for (const [typeChar, tier] of Object.entries(typeToTier)) {
          if (!respinGameType.includes(typeChar) && controllers[tier]) {
            controllers[tier].deactivate();
          } else if (controllers[tier]) {
            controllers[tier].playIdle();
          }
        }
      }

      // 5. Apply feature skin for active pot text labels
      controllerRef.current.setFeatureSkin(respinGameType || null);

      // 6. Show respin counter and set to current value
      controllerRef.current.setRespinCounterVisible(true);
      const counterCtrl = potCollectionRespinCounterRef?.current;
      if (counterCtrl) {
        counterCtrl.playAnimation(pcState.respinsLeft ?? 3);
      }

      // 7. Feature background music
      soundManager.fadeIn(SOUND_NAME.feature_background_music, 500);

      // 8. Show the respins left popup (after FR info popup closes)
      //    Skip when 0 respins left — popup is pointless, just unlock UI after brief delay
      showReloadPopupWhenReady(pcState.respinsLeft ?? 3, false);

      return; // Skip cloud transition entirely
    }

    // ── Normal intro flow: play cloud transition ──
    // Re-lock UI for the whole cloud window. Win processing can clear
    // criticalAnimationPlaying before intro closes; ButtonPlay is still mounted
    // until midpoint (potCollectionActive flips there). Orientation changes can
    // also clear userInterfaceDisabled via IosFullscreenOverlay.
    dispatch(setCriticalAnimationPlaying(true));
    dispatch(setUserInterfaceDisabled(true));

    // Fade out base game music at the start of the transition (ambient auto-paired)
    soundManager.fadeOut(SOUND_NAME.background_music, 500);

    controllerRef.current.playTransition("bonus", () => {
      // ══ MIDPOINT: screen fully covered by clouds ══

      // 0. Force bonus game to start in normal auto speed (client requirement).
      //    Turbo should not carry over — player must manually enable it.
      //    Use NoSave variant so localStorage keeps the player's turbo preference
      //    for restoration when returning to base game.
      const currentSpeed = store.getState().settings.autoPlaySpeed;
      if (isTurboLike(currentSpeed)) {
        dispatch(setAutoplaySpeedNoSave('auto'));
      }

      // 1. Clean up base game win animations → transition to idle
      triggerInitialIdleAnimations(true);

      // 2. Stop any active win animation cycling
      dispatch(setWinAnimationsPlaying(false));

      // 3. Activate bonus scene — React will render PotCollectionReelSet alongside base game
      dispatch(setPotCollectionActive(true));
      // Note: trigger-coin idle animations are handled by PotCollectionOverlaySymbol on mount
      // via initialRespinScreen selector — no timing games needed here.

      // 4. Reset trigger flag (one-shot)
      dispatch(setPotCollectionTriggered(false));

      // 5. Deactivate non-triggered POTs, switch triggered ones to idle
      const currentState = store.getState();
      const respinGameType = currentState.potCollection?.respinGameType || "";
      const typeToTier = { M: "mystery", D: "double", C: "collect" };
      const controllers = potControllersRef?.current;
      if (controllers) {
        for (const [typeChar, tier] of Object.entries(typeToTier)) {
          if (!respinGameType.includes(typeChar) && controllers[tier]) {
            controllers[tier].deactivate();
          } else if (controllers[tier]) {
            controllers[tier].playIdle();
          }
        }
      }

      // 5b. Apply feature skin for active pot text labels
      const featureSkinName = respinGameType || null;
      controllerRef.current?.setFeatureSkin(featureSkinName);

      // 6. UI stays locked — entry sequence (ejection + features) will unlock
      //    via setCriticalAnimationPlaying(false) when entryPhase reaches "ready"

      // 7. Show respin counter container (was alpha 0.001) — animation plays later at entry sequence
      controllerRef.current?.setRespinCounterVisible(true);

      // 8. Fade in feature background + ambient music (ambient auto-paired)
      soundManager.fadeIn(SOUND_NAME.feature_background_music, 500);
    }, () => {
      // ══ COMPLETE: transition animation fully finished (clouds removed) ══
      // Clear the transition flag so spacebar / UI becomes interactive.
      dispatch(setPotCollectionTransitionActive(false));
    });
  }, [showPotCollectionIntro, potCollectionTriggered, dispatch, dimReelSetRef, triggerInitialIdleAnimations, potControllersRef, potCollectionReelRefs, potCollectionRespinCounterRef, showReloadPopupWhenReady]);

  // ── POT Collection Outro → Base Game Transition (reverse of entry) ──────────
  // When the outro popup finishes its "out" animation, it dispatches
  // setPotCollectionOutroTransitionActive(true). This hook plays the cloud
  // transition in reverse (bonus → main) and resets all bonus state at midpoint.
  const potCollectionOutroTransitionActive = useSelector(selectPotCollectionOutroTransitionActive);
  const prevOutroTransitionRef = useRef(potCollectionOutroTransitionActive);

  useEffect(() => {
    if (!FEATURES_CONFIG.POT_COLLECTION.ENABLED) return;
    const prev = prevOutroTransitionRef.current;
    prevOutroTransitionRef.current = potCollectionOutroTransitionActive;

    // Only fire on flag activation (false → true)
    if (prev || !potCollectionOutroTransitionActive) return;
    if (!controllerRef.current) return;

    controllerRef.current.playTransition("main", () => {
      // ══ MIDPOINT: screen fully covered by clouds ══

      // 0. Restore player's pre-bonus speed from localStorage.
      //    Bonus entry used setAutoplaySpeedNoSave to keep the persisted value intact.
      const savedUISettings = StorageManager.loadUISettings();
      const savedSpeed = savedUISettings.autoPlaySpeed || 'auto';
      const liveSpeed = store.getState().settings.autoPlaySpeed;
      if (liveSpeed !== savedSpeed) {
        dispatch(setAutoplaySpeedNoSave(savedSpeed));
      }

      // 1. Reset entire pot collection slice to initialState
      //    This sets potCollectionActive = false → React swaps PotCollectionReelSet → ReelSet
      dispatch(resetPotCollection());

      // 1b. Clear featureState in gameSettingsSlice (type: null, gameMode: "base")
      //     Without this, getSpinParams() still sees type=="bonus" → sends "respin"
      dispatch(clearFeatureStateToBase());

      // 1c. Reset spinSlice feature spin state so next spin is treated as a base game spin
      //     CRITICAL: featureSpinStatus must be "succeeded" for setFeatureSpinComplete to take effect
      //     (spinSlice reducer guards on this). Force it first to avoid silent drop.
      dispatch(setFeatureSpinActive(false));
      dispatch(setFeatureSpinComplete(true));

      // 2. Restore POTs to API stages and play idle (under cloud cover).
      //    Triggered POTs: BE resets them to 0.
      //    Non-triggered POTs: BE keeps their stage — must NOT wipe to 0 on FE.
      const controllers = potControllersRef?.current;
      if (controllers) {
        const keyMap = { D: "double", M: "mystery", C: "collect" };
        const applyPotStagesFromApi = (potProgress) => {
          for (const [apiChar, potType] of Object.entries(keyMap)) {
            const ctrl = controllers[potType];
            if (!ctrl) continue;
            const apiStage = potProgress?.[apiChar]?.stage ?? 0;
            // Direct stage assign — no growth animation; sync under clouds
            ctrl.stage = apiStage;
            ctrl.reactivate();
            ctrl.playIdle();
          }
        };

        // ── Which stages, live or replayed ──
        //
        // In a REPLAY, `potProgress` holds the stages of the round being
        // watched, and the player's own are parked in the slice's pre-replay
        // snapshot. Read them from there, so coming back from a replayed bonus
        // hands the orbs back on THIS beat — under the clouds, the same moment
        // normal play restores them — instead of leaving them at the replayed
        // round's high-water mark until the history board is closed.
        const inReplay = !!store.getState().replay?.sessionActive;
        applyPotStagesFromApi(
          inReplay
            ? selectPreReplayPotProgress(store.getState())
            : store.getState().game?.potProgress,
        );

        // Fresh BE sync — re-apply when potProgress updates so FE matches server.
        // Skipped in replay: syncGameState runs the full live initGame(), which
        // writes and clears the player's real localStorage (base/bonus/trigger/
        // free-rounds rounds) and hits the live API — a standalone replay page
        // has no API at all.
        if (!inReplay) {
          dispatch(syncGameState({ syncPots: true })).then(() => {
            applyPotStagesFromApi(store.getState().game?.potProgress);
          });
        }
      }

      // 3. Restore pot text labels ("COLLECT", "MYSTERY", "DOUBLE")
      controllerRef.current?.showAllPotTexts();

      // 4. Hide bonus-only UI elements
      controllerRef.current?.setRespinCounterVisible(false);
      controllerRef.current?.setCoinCounterVisible(false);

      // 5. Start idle animations on base game symbols.
      //    Deferred one frame so React has flushed the reel swap (resetPotCollection → ReelSet
      //    re-mounts) before we read currentSymbolId from the reel sprites.
      //    Same pattern as FreeSpinControlContext exitFreeSpinReveal.
      requestAnimationFrame(() => {
        triggerInitialIdleAnimations(false);
      });

      // 6. Swap music: feature → base game (ambient auto-paired)
      soundManager.fadeOut(SOUND_NAME.feature_background_music, 500);
      soundManager.fadeIn(SOUND_NAME.background_music, 500);

      // 7. Reset useWinController refs so the first base game win plays correctly.
      //    Without this: animationCompletedRef stays true (set during PC trigger spin),
      //    animatedRoundsRef still has the trigger-round dedup entry, and
      //    criticalAnimationPlaying may still be true — all of which block win animations.
      dispatch(closeConnectionPopup()); // Safety: clear stale connection popup from bonus respins
      dispatch(setResetMainGameVisualEffects(true)); // triggers animationCompletedRef=false + animatedRoundsRef.clear()

      // BLOCK UI: Keep interface locked during transition + balance sync.
      // Flags may already be false from bonus game logic, so explicitly set true.
      dispatch(setCriticalAnimationPlaying(true));
      dispatch(setUserInterfaceDisabled(true));
    }, async () => {
      // ══ COMPLETE: transition animation fully finished (clouds removed) ══
      dispatch(setPotCollectionOutroTransitionActive(false));

      // Credit the bonus winnings — this is the moment the player sees the
      // balance go up after returning to base game.
      //
      // Replay: never hit the server (syncGameState would fetch the player's
      // REAL wallet, and on a standalone replay page __OPTIONS__.api is empty
      // so there's nothing to fetch). Credit the round's own total instead.
      // potCollection.currentRound.win carries `balance.game`, which is the
      // round's CUMULATIVE win, and applyReplayWin only adds what hasn't been
      // credited yet — so this settles the bonus total exactly once however
      // much the respins already revealed.
      // Skip during FR — useFreeRoundsCompletion handles balance sync after popups close
      const stateAtOutro = store.getState();
      const frActive = stateAtOutro.freeRounds.status === "active" || stateAtOutro.freeRounds.completionPending;
      if (frActive) {
        // Mid-campaign bonus: nothing is credited here in live play either —
        // free-rounds winnings (bonus included) settle once, at the campaign's
        // end, via useFreeRoundsCompletion. Checked FIRST so replay follows the
        // same rule; crediting here would pay the bonus out a round early.
      } else if (stateAtOutro.replay?.sessionActive) {
        dispatch(applyReplayWin(stateAtOutro.potCollection?.currentRound?.win || 0));
      } else {
        await dispatch(syncGameState({ syncBalance: true }));
      }

      // Re-enable UI ONLY AFTER balance sync completes — prevents spacebar/click
      // from triggering a spin with stale balance during the transition window
      dispatch(setCriticalAnimationPlaying(false));
      dispatch(setUserInterfaceDisabled(false));

      // Resume autoplay ONLY AFTER balance sync completes
      const pausedSpins = store.getState().settings.autoplayPausedSpins;
      if (pausedSpins !== null) {
        dispatch(resumeAutoPlay());
      }
    });
  }, [potCollectionOutroTransitionActive, dispatch, triggerInitialIdleAnimations, potControllersRef]);

  // ── POT Blitz Teaser (GDD §10.1) ──────────────────────────────────────────
  // Starts after 10s of no spinning, then fires every 5-10s on 1 random pot.
  // Resets on every spin. Disabled during turbo, super turbo, autospins, and bonus game.
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
  const autoPlayActive = useSelector(selectAutoPlayActive);


  useEffect(() => {
    if (!FEATURES_CONFIG.POT_CHARGING.ENABLED) return;

    // Blitz disabled during turbo, super turbo, autospins, bonus game (triggered OR active), and intro popup (GDD §10.1 + §22.4: base game only)
    if (isTurboLike(autoPlaySpeed) || autoPlayActive || isSpinning || potCollectionTriggered || potCollectionActive || introPopupActive) return;

    const potTypes = ["collect", "double", "mystery"];
    let timer = null;

    const scheduleBlitz = (delay) => {
      timer = new TickerTimeout(() => {
        const controllers = potControllersRef?.current;
        if (controllers) {
          const randomPot = potTypes[Math.floor(Math.random() * potTypes.length)];
          controllers[randomPot]?.playBlitz();
        }
        // Next blitz: 5-10s
        scheduleBlitz(5000 + Math.random() * 5000);
      }, delay);
    };

    // First blitz after 10s idle cooldown
    scheduleBlitz(10000);

    return () => {
      if (timer) timer.clear();
    };
  }, [autoPlaySpeed, autoPlayActive, isSpinning, potCollectionTriggered, potCollectionActive, introPopupActive, potControllersRef]);

  // ── POT Anticipation (GDD §10.2) ─────────────────────────────────────────
  // When anticipation state activates, fire playAnticipation() on all 3 POTs.
  // Background storm effect will be wired here when spine assets arrive.
  const currentRound = useSelector(selectCurrentRound);
  // GDD §9.1 + §7.3: anticipation animations are fully disabled during super turbo
  const anticipationActive = currentRound?.anticipation && isSpinning && !isSuperTurbo(autoPlaySpeed);

  // Track symbolHeight via ref so the anticipation effect doesn't re-trigger on resize.
  // Resize during a 2-3s anticipation shake is harmless — amplitude stays stable.
  // Re-triggering the effect on resize causes the stuck-shake bug: the ramp-out (600ms)
  // races with the new ramp-in, leaving the shake loop running forever.
  symbolHeightRef.current = symbolHeight;

  useEffect(() => {
    if (!FEATURES_CONFIG.TENSION.ANTICIPATION?.ENABLED) return;
    if (!anticipationActive) return;

    // ── POT anticipation animations ───────────────────────────────────────
    if (FEATURES_CONFIG.POT_CHARGING.ENABLED) {
      const controllers = potControllersRef?.current;
      if (controllers) {
        // Start looping anticipation on all 3 POTs
        for (const controller of Object.values(controllers)) {
          if (!controller?.spine?.state) continue;
          const anim = controller._resolveAnim("anticipation", controller.stage);
          if (anim) {
            controller.spine.state.setAnimation(0, anim, true); // loop
          }
        }
      }
    }

    // ── Background storm animation (GDD §10.3 step 3) ────────────────────
    // Plays anticipation_in → anticipation_loop on track 1 of game_view spine.
    // Tension effect starts when intro finishes and loop begins.
    const controller = controllerRef.current;
    controller?.playAnticipation(() => {
      // ── Bone-level tension effect (reel + pots) ──────────────────────
      // Starts after anticipation_in completes so the storm is fully built
      controller?.startTensionEffect(symbolHeightRef.current);
    });

    // Cleanup: stop all anticipation effects when spin ends
    return () => {
      // Restore POTs to idle — or play win if bonus was triggered
      if (FEATURES_CONFIG.POT_CHARGING.ENABLED) {
        const controllers = potControllersRef?.current;
        if (controllers) {
          const currentFS = store.getState().game.featureState;
          const isBonusTrigger =
            (currentFS?.type === "bonus") &&
            currentFS?.phase === "intro";
          const respinGameType = currentFS?.data?.respinGameType || "";
          const typeToTier = { M: "mystery", D: "double", C: "collect" };

          for (const [typeChar, tier] of Object.entries(typeToTier)) {
            const ctrl = controllers[tier];
            if (!ctrl?.spine?.state) continue;

            if (isBonusTrigger && respinGameType.includes(typeChar)) {
              // Triggered pot — restore to win loop (already at stage 11 on reload)
              ctrl.playWinLoop();
            } else {
              // Non-triggered or no bonus — restore to idle
              const idle = ctrl._resolveAnim("idle", ctrl.stage);
              if (idle) {
                ctrl.spine.state.setAnimation(0, idle, true);
              }
            }
          }
        }
      }

      // Stop background storm (plays outro → clears track 1)
      controller?.stopAnticipation();

      // Stop bone-level shake
      controller?.stopTensionEffect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anticipationActive, potControllersRef]);

  return (
    <Container
      ref={containerRef}
      name="game_view"
      sortableChildren
      zIndex={0}
    >
      {/* Reel container — injected into spine's attachment_slot_placeholder bone slot */}
      <Container
        ref={reelContainerRef}
        name="reel_container"
        x={REELSET_X_OFFSET}
        y={REELSET_Y_OFFSET}
        alpha={(menuActive && Settings.APP.UI_THEME === "primary") ? 0.001 : 1}
        sortableChildren
        eventMode="passive"
      >
        {/* Divider overlay — swaps between base and feature texture when bonus skin is active */}
        {(() => {
          const textureName = potCollectionActive ? "divider_feature" : "divider_base";
          const config = potCollectionActive ? DIVIDER_FEATURE_CONFIG : DIVIDER_BASE_CONFIG;
          if (!Assets.cache.has(textureName)) return null;
          return (
            <Sprite
              texture={Assets.get(textureName)}
              name={textureName}
              anchor={0.5}
              width={
                symbolWidth *
                Settings.REEL_COUNT *
                config.widthMultiplier
              }
              height={
                symbolHeight *
                Settings.SYMBOLS_VISIBLE *
                config.heightMultiplier
              }
              x={config.xOffset}
              y={config.yOffset}
              zIndex={0}
            />
          );
        })()}

        {/* Base game ReelSet — always mounted to preserve refs/controllers; invisible during Pot Collection */}
        <Container alpha={potCollectionActive ? 0.001 : 1}>
          <ReelSet
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            dividerDimensions={dividerDimensions}
            reelSpacing={reelSpacing}
          />
        </Container>

        {/* Pot Collection ReelSet — mounted only when PC is active */}
        {potCollectionActive && (
          <PotCollectionReelSet
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            reelSpacing={reelSpacing}
          />
        )}

        <DimReelSet
          ref={dimReelSetRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
        />
      </Container>

      {/* Overlay container — injected into spine's attachment_slot_placeholder_front bone slot */}
      <Container
        ref={overlayContainerRef}
        name="overlay_reel_container"
        x={REELSET_X_OFFSET}
        y={REELSET_Y_OFFSET}
        zIndex={2}
        sortableChildren
        eventMode="passive"
      >
        {/* Base game OverlayReelSet — always mounted to preserve refs/controllers; invisible during Pot Collection */}
        <Container alpha={potCollectionActive ? 0.001 : 1}>
          <OverlayReelSet
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            reelSpacing={reelSpacing}
          />
        </Container>

        {/* Pot Collection OverlayReelSet — mounted only when PC is active */}
        {potCollectionActive && (
          <PotCollectionOverlayReelSet
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            reelSpacing={reelSpacing}
          />
        )}

        {/* Win text overlays — global (show all wins) + per-line (line cycling) */}
        {FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED && (
          <Container name="win_text_overlay" zIndex={6}>
            <WinText
              ref={globalLineWinTextRef}
              fontSizeMultiplier={WIN_TEXT_CONFIG.GLOBAL_WIN_FONT_SIZE}
              hideOnBigWin={true}
            />
            <WinText
              ref={lineWinTextRef}
              fontSizeMultiplier={WIN_TEXT_CONFIG.LINE_WIN_FONT_SIZE}
              hideOnBigWin={true}
            />
          </Container>
        )}

        {FEATURES_CONFIG.MINI_PAYTABLE.ENABLED && <MiniPaytable />}

        {FEATURES_CONFIG.WIN_BOX.ENABLED && (
          <WinBoxContainer
            ref={winBoxContainerRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            reelSpacing={reelSpacing}
          />
        )}
      </Container>

      {/* Tension reel container — injected into overlay spine slot (above DimReelSet) */}
      <Container
        ref={tensionContainerRef}
        name="tension_reel_container"
        x={REELSET_X_OFFSET}
        y={TENSION_Y_OFFSET}
        zIndex={1}
        sortableChildren
        eventMode="passive"
      >
        {FEATURES_CONFIG.TENSION.ENABLED && <TensionReelSet />}
      </Container>

      {/* Full-screen invisible sprite for click detection */}
      {FEATURES_CONFIG.BACKGROUND.FULLSCREEN_CLICK_SPRITE && (
        <Sprite
          texture={PIXI.Texture.EMPTY}
          x={0}
          y={0}
          width={spriteDimensions.width}
          height={spriteDimensions.height}
          zIndex={0}
          eventMode={isFeatureActive || !backgroundClickIsActionable ? "none" : "static"}
          cursor={backgroundClickIsActionable ? "pointer" : "default"}
          pointertap={handleBackgroundClick}
        />
      )}

      {/* POT Charging trail container — particle trails from Zeus symbols to POTs */}
      {FEATURES_CONFIG.POT_CHARGING.ENABLED && (
        <Container
          ref={potTrailContainerRef}
          name="pot_trail_container"
          zIndex={20}
          sortableChildren
          eventMode="passive"
        />
      )}

      {/* Value collection trail layer — must render ABOVE the coin collector spine */}
      {FEATURES_CONFIG.POT_COLLECTION.ENABLED && (
        <PotCollectionCollectionLayer
          ref={potCollectionCollectionLayerRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
        />
      )}

      {/* Respin counter — mounted only when PC is active, fresh controller each bonus */}
      {FEATURES_CONFIG.POT_COLLECTION.ENABLED && potCollectionActive && <PotCollectionRespinCounter />}

    </Container>
  );
});

GameView.displayName = "GameView";

export default GameView;
